import { createContext, useRef, ReactNode, RefObject, MutableRefObject, useState } from 'react';

/**
 * What the learner still has to do on an assignment activity. `null` outside assignments and
 * once the file is submitted, so the navigation falls back to its regular Scroll down / Next.
 */
export type AssignmentStep = 'upload' | 'submit' | null;

type CourseNavigationContextProps = {
  reflectionInputRef: RefObject<HTMLDivElement> | null;
  submitQuizButtonRef: RefObject<HTMLDivElement> | null;
  submitQuizButtonHighlightRef: RefObject<VoidFunction | null>;
  forceNavigationUpdateRef: RefObject<VoidFunction | null>;
  // Whether the learner has reached the end of the active Page activity - either by
  // scrolling to the bottom or because the content fits on the screen and cannot be
  // scrolled at all. Maintained by EnhancedCourseNavigation, which is the only place that
  // measures the scroll container. Page activities complete on unmount, so this flag lets
  // us grant progress only when the end was actually reached (not on plain Exit /
  // browser-back). See PT-5785.
  hasReachedEndRef: MutableRefObject<boolean> | null;
  isPageLoading: boolean;
  setIsPageLoading: (value: boolean) => void;
  retakeQuizButtonRef: RefObject<HTMLDivElement> | null;
  quizSubmitted: boolean;
  quizHasPassed: boolean;
  setQuizSubmitted: (value: boolean) => void;
  setQuizHasPassed: (value: boolean) => void;
  assignmentStep: AssignmentStep;
  setAssignmentStep: (value: AssignmentStep) => void;
  // Set by the assignment page to what the FAB does for the current step - open the file
  // picker, or highlight the Submit button. The navigation only scrolls before calling it.
  assignmentStepActionRef: MutableRefObject<VoidFunction | null> | null;
};

const initialProps: CourseNavigationContextProps = {
  reflectionInputRef: null,
  submitQuizButtonRef: null,
  submitQuizButtonHighlightRef: null as unknown as RefObject<VoidFunction>,
  forceNavigationUpdateRef: null as unknown as RefObject<VoidFunction>,
  hasReachedEndRef: null,
  isPageLoading: false,
  setIsPageLoading: () => undefined,
  retakeQuizButtonRef: null,
  quizSubmitted: false,
  quizHasPassed: false,
  setQuizSubmitted: () => undefined,
  setQuizHasPassed: () => undefined,
  assignmentStep: null,
  setAssignmentStep: () => undefined,
  assignmentStepActionRef: null,
};

export const CourseNavigationContext = createContext(initialProps);

type CourseNavigationProviderProps = {
  children: ReactNode;
};

export const CourseNavigationProvider = ({ children }: CourseNavigationProviderProps): React.ReactElement => {
  const [isPageLoading, setIsPageLoading] = useState(false);
  const forceNavigationUpdateRef = useRef<VoidFunction | null>(null);
  const hasReachedEndRef = useRef<boolean>(false);
  const reflectionInputRef = useRef<HTMLDivElement>(null);
  const submitQuizButtonRef = useRef<HTMLDivElement>(null);
  const submitQuizButtonHighlightRef = useRef<VoidFunction | null>(null);
  const retakeQuizButtonRef = useRef<HTMLDivElement>(null);
  const [quizSubmitted, setQuizSubmitted] = useState(false);
  const [quizHasPassed, setQuizHasPassed] = useState(false);
  const [assignmentStep, setAssignmentStep] = useState<AssignmentStep>(null);
  const assignmentStepActionRef = useRef<VoidFunction | null>(null);

  return (
    <CourseNavigationContext.Provider
      value={{
        forceNavigationUpdateRef,
        hasReachedEndRef,
        reflectionInputRef,
        submitQuizButtonRef,
        submitQuizButtonHighlightRef,
        isPageLoading,
        setIsPageLoading,
        retakeQuizButtonRef,
        quizSubmitted,
        quizHasPassed,
        setQuizSubmitted,
        setQuizHasPassed,
        assignmentStep,
        setAssignmentStep,
        assignmentStepActionRef,
      }}
    >
      {children}
    </CourseNavigationContext.Provider>
  );
};
